import React, { FC, ReactNode } from 'react'
import { Alert, Box, Grid, Typography } from '@mui/material'
import format from 'date-fns/format'
import { ContractorTransfer, ContractorTransferStatusEnum } from 'src/api'
import { InnerLink } from 'src/components/ui'
import { ContractorTransferStatusChip } from 'src/components/columns/contractorTransfer'
import { getTransferStages, getTransferStatusMeta } from 'src/components/columns/contractorTransferHelpers'

interface ContractorTransferInfoProps {
  transfer: ContractorTransfer
}

interface InfoItem {
  name: string
  value: ReactNode
}

const formatDate = (value?: string | null): string => (value ? format(new Date(value), 'PPpp') : '-')

export const ContractorTransferInfo: FC<ContractorTransferInfoProps> = ({ transfer }) => {
  const { status, error, sourceContractorId, targetContractorId, sourceContractor, targetContractor, author } = transfer
  const { description } = getTransferStatusMeta(status)
  const failedStage = getTransferStages(transfer).find((stage) => stage.state === 'failed')

  const items: InfoItem[] = [
    {
      name: 'Status',
      value: (
        <>
          <ContractorTransferStatusChip status={status} />
          {description && (
            <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
              {description}
            </Typography>
          )}
        </>
      )
    },
    { name: 'Source', value: <InnerLink to={`/contractors/${sourceContractorId}`}>{sourceContractor?.name || `#${sourceContractorId}`}</InnerLink> },
    { name: 'Target', value: <InnerLink to={`/contractors/${targetContractorId}`}>{targetContractor?.name || `#${targetContractorId}`}</InnerLink> },
    { name: 'Author', value: author?.id ? <InnerLink to={`/users/${author.id}`}>{author.name}</InnerLink> : author?.name || '-' },
    { name: 'Created', value: formatDate(transfer.createdAt) },
    { name: 'Started', value: formatDate(transfer.startedAt) },
    { name: 'Data moved', value: formatDate(transfer.committedAt) },
    { name: 'Finished', value: formatDate(transfer.finishedAt) }
  ]

  return (
    <Box width="100%">
      {error &&
        (status === ContractorTransferStatusEnum.Cancelled ? (
          // the failure that preceded the cancellation is history, not a problem to act on
          <Alert severity="info" sx={{ mb: 2 }}>
            {`Before it was cancelled: ${error}`}
          </Alert>
        ) : (
          <Alert severity="error" sx={{ mb: 2 }}>
            {failedStage ? `${failedStage.label} failed: ${error}` : error}
          </Alert>
        ))}
      <Grid container spacing={0}>
        {items.map((item) => (
          <Grid key={item.name} item xs={12} sx={{ py: 0.5 }}>
            <Typography variant="body1" component="div">
              <Typography variant="body1" fontWeight={500} display="inline">
                {item.name}:
              </Typography>{' '}
              {item.value}
            </Typography>
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}
